// Given the full sequence of parsed events (caller passes array of parsed
// events after stream ends — simplest option per phase spec), find exactly
// one `result` event, validate structured_output, and cross-check session_id
// / run_id.
//
// D06 prose-JSON fallback parsing (findLastBalancedJsonObject etc.) split
// into terminal-result-prose-json-fallback.ts to keep this file ≤200 lines.
import { AutopilotResultSchema } from "./autopilot-result-schema.js";
import type { ParsedEvent } from "./stream-json-event-parser.js";
import {
  extractProseJsonResult,
  joinIssuePath,
  rawResultTailOf,
} from "./terminal-result-prose-json-fallback.js";

// D06 finding 1 (real S1 smoke): the pinned binary can return well-formed
// JSON matching the result schema as prose inside `result` text (sometimes
// fenced in ```json) instead of populating `structured_output`, even with
// --json-schema passed. `resultSource` records which path produced the
// validated object so callers/evidence reports can distinguish "the
// contract worked as designed" from "we fell back to parsing prose."
export type TerminalResultSource = "structured_output" | "prose_json" | "none";

export interface TerminalResultContractError {
  kind: "contract_error";
  reason: string;
  resultSource: TerminalResultSource;
  // Observability (P13 Agent A): raw `result` text tail (last ~4000 chars,
  // UNREDACTED here — caller redacts before persisting, same pattern as
  // attempt-forensics.ts's own bindingEvidence path) so a
  // `no_result_json_found`/`invalid_structured_output_schema` verdict is
  // debuggable from disk instead of only "the model got it wrong, no idea
  // which field" (dogfood vòng 3/5 open question #8: 0 report files ever
  // captured for these outcomes). `undefined` when there is no `result` text
  // to show (e.g. missing_result_event).
  rawResultTail?: string;
  // Field-name-only zod issue paths (`ZodError.issues[].path`, dot-joined,
  // e.g. "evidence[0].sha256") for `invalid_structured_output_schema` only —
  // NEVER includes issue.message/received value (could echo worker-authored
  // strings containing secrets/PII, same rule as forensics evidence).
  schemaErrorPaths?: string[];
}

export interface TerminalResultOk {
  kind: "ok";
  resultEvent: Extract<ParsedEvent, { kind: "result" }>["raw"];
  structuredOutput: unknown;
  resultSource: TerminalResultSource;
}

export type TerminalResultExtraction = TerminalResultOk | TerminalResultContractError;

export interface TerminalResultExpectations {
  sessionId: string;
  runId?: string; // present when the caller tracks a run_id field on result events
}

export function extractTerminalResult(
  events: ParsedEvent[],
  expected: TerminalResultExpectations,
): TerminalResultExtraction {
  const resultEvents = events.filter(
    (e): e is Extract<ParsedEvent, { kind: "result" }> => e.kind === "result",
  );

  if (resultEvents.length === 0) {
    return { kind: "contract_error", reason: "missing_result_event", resultSource: "none" };
  }
  if (resultEvents.length > 1) {
    return { kind: "contract_error", reason: "two_result_events", resultSource: "none" };
  }

  const resultEvent = resultEvents[0] as Extract<ParsedEvent, { kind: "result" }>;
  const raw = resultEvent.raw;

  if (raw.session_id !== undefined && raw.session_id !== expected.sessionId) {
    return { kind: "contract_error", reason: "session_id_mismatch", resultSource: "none" };
  }

  if (expected.runId !== undefined) {
    const runIdOnEvent = (raw as { run_id?: string }).run_id;
    if (runIdOnEvent !== undefined && runIdOnEvent !== expected.runId) {
      return { kind: "contract_error", reason: "run_id_mismatch", resultSource: "none" };
    }
  }

  if (raw.structured_output !== undefined) {
    const validated = AutopilotResultSchema.safeParse(raw.structured_output);
    if (!validated.success) {
      return {
        kind: "contract_error",
        reason: "invalid_structured_output_schema",
        resultSource: "none",
        rawResultTail: rawResultTailOf(raw.result ?? JSON.stringify(raw.structured_output)),
        schemaErrorPaths: validated.error.issues.map((issue) => joinIssuePath(issue.path)),
      };
    }
    return {
      kind: "ok",
      resultEvent: raw,
      structuredOutput: validated.data,
      resultSource: "structured_output",
    };
  }

  // D06 finding 1: structured_output absent (real behavior on pinned binary
  // even with --json-schema) — fall back to parsing the last balanced JSON
  // object out of the free-text `result` string and validating it against
  // the same schema. Still a contract_error if neither source yields a valid
  // object (spec requirement: "still contract_error when neither yields a
  // valid object").
  const proseCandidate = extractProseJsonResult(raw.result);
  if (proseCandidate === undefined) {
    return {
      kind: "contract_error",
      reason: "no_result_json_found",
      resultSource: "none",
      rawResultTail: rawResultTailOf(raw.result),
    };
  }

  const proseValidated = AutopilotResultSchema.safeParse(proseCandidate);
  if (!proseValidated.success) {
    return {
      kind: "contract_error",
      reason: "invalid_structured_output_schema",
      resultSource: "none",
      rawResultTail: rawResultTailOf(raw.result),
      schemaErrorPaths: proseValidated.error.issues.map((issue) => joinIssuePath(issue.path)),
    };
  }

  return {
    kind: "ok",
    resultEvent: raw,
    structuredOutput: proseValidated.data,
    resultSource: "prose_json",
  };
}
